import { useCallback, useSyncExternalStore } from 'react'

// Web Storage is external to React, so reads go through `useSyncExternalStore`: every write made
// through `useLocalStorage` notifies the subscribers, and a hook whose value changed re-renders.
const listeners = new Set<() => void>()

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const storageFor = (session: boolean) => (session ? sessionStorage : localStorage)

const write = (key: string, value: string | null, session: boolean) => {
  if (value != null) {
    storageFor(session).setItem(key, value)
  } else {
    storageFor(session).removeItem(key)
  }
  listeners.forEach(listener => listener())
}

export const useLocalStorage = (
  key: string,
  initial: string | null = null,
  options: { session: boolean } = { session: false },
) => {
  const { session } = options
  const persisted = useSyncExternalStore(subscribe, () => storageFor(session).getItem(key))
  const value = persisted ?? initial
  const setValue = useCallback(
    (newValue: string | null) => write(key, newValue, session),
    [key, session],
  )
  return [value, setValue] as const
}
